/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { css, jsx } from '@compiled/react';

import type { HierarchyData } from '@atlassian/reporting-lines/hierarchy-chart';

import type {
	Collaborator,
	PersonProfileCardErrorType,
	PersonProfileData,
	RecentWorkUrls,
} from '../../types';

import { CardContainer } from './CardContainer';
import { CompactVariant } from './CompactVariant';
import { ErrorCard } from './ErrorCard';
import { FullVariant } from './FullVariant';
import { LoadingSkeleton } from './LoadingSkeleton';

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'user-profile-preview-container',
});

const compactOnly = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container user-profile-preview-container (min-width: 481px)': {
		display: 'none',
	},
});

const fullOnly = css({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container user-profile-preview-container (min-width: 481px)': {
		display: 'block',
	},
});

interface TenureData {
	beforePercent: number;
	beforeCount: number;
	afterCount: number;
}

export interface ContentSwitchProps {
	isLoading: boolean;
	error?: PersonProfileCardErrorType;
	data?: PersonProfileData;
	hierarchyData?: HierarchyData;
	recentWorkData?: RecentWorkUrls;
	collaborators?: Collaborator[];
	tenureData?: TenureData;
	testId?: string;
	cloudId: string;
}

/**
 * Internal component that switches between loading, error, and content variants based on state.
 * Renders both compact and full variants when data exists; CSS container queries control visibility.
 */
function ContentSwitch({
	isLoading,
	error,
	data,
	hierarchyData,
	recentWorkData,
	collaborators,
	tenureData,
	testId,
	cloudId,
}: ContentSwitchProps) {
	return (
		<div css={containerRoot} data-testid={testId}>
			<CardContainer>
				{isLoading ? (
					<LoadingSkeleton />
				) : error ? (
					<ErrorCard error={error} />
				) : data ? (
					<>
						<div css={compactOnly} data-testid={testId ? `${testId}-layout-compact` : undefined}>
							<CompactVariant
								data={data}
								hierarchyData={hierarchyData}
								recentWorkData={recentWorkData}
								collaborators={collaborators}
								tenureData={tenureData}
								testId={testId}
								cloudId={cloudId}
							/>
						</div>
						<div css={fullOnly} data-testid={testId ? `${testId}-layout-full` : undefined}>
							<FullVariant
								data={data}
								hierarchyData={hierarchyData}
								recentWorkData={recentWorkData}
								collaborators={collaborators}
								tenureData={tenureData}
								testId={testId}
								cloudId={cloudId}
							/>
						</div>
					</>
				) : null}
			</CardContainer>
		</div>
	);
}

export { ContentSwitch };
